// PIQ Advocate Engine - Opus 4.5 Build
// Discovery Page - Mention inbox and advocate discovery board

import React, { useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { 
  Search,
  Filter,
  MessageSquare,
  User,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Minus,
  RefreshCw,
  Link2,
  ChevronDown
} from 'lucide-react'
import { 
  useMentions, 
  useAdvocates,
  useLinkMentions,
  useRecalculateScores,
  type MentionFilters,
  type AdvocateFilters,
  type AdvocateMention,
  type Advocate
} from '../../lib/advocate-opus-data-service'

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdvocateOpusDiscovery() {
  const [location, navigate] = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.split('?')[1] || ''), [location])
  const [view, setView] = useState<'mentions' | 'advocates'>(
    searchParams.get('view') === 'advocates' ? 'advocates' : 'mentions'
  )
  
  // Filter state
  const [mentionFilters, setMentionFilters] = useState<MentionFilters>({
    platform: searchParams.get('platform') || undefined,
    sentiment: searchParams.get('sentiment') || undefined,
    status: searchParams.get('status') || undefined,
    search: searchParams.get('search') || undefined,
    page: 1,
    limit: 25,
  })

  const [advocateFilters, setAdvocateFilters] = useState<AdvocateFilters>({
    tier: searchParams.get('tier') || undefined,
    status: searchParams.get('advocate_status') || undefined,
    search: searchParams.get('search') || undefined,
    page: 1,
    limit: 25,
  })

  // Queries
  const { data: mentionsData, isLoading: mentionsLoading, refetch: refetchMentions } = useMentions(mentionFilters)
  const { data: advocatesData, isLoading: advocatesLoading, refetch: refetchAdvocates } = useAdvocates(advocateFilters)
  
  // Mutations
  const linkMentions = useLinkMentions()
  const recalculateScores = useRecalculateScores()

  const handleSearch = (value: string) => {
    if (view === 'mentions') {
      setMentionFilters(f => ({ ...f, search: value || undefined, page: 1 }))
    } else {
      setAdvocateFilters(f => ({ ...f, search: value || undefined, page: 1 }))
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-8 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">Advocate Discovery</h1>
                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                  Opus Build
                </span>
              </div>
              <p className="text-gray-500 mt-1">Find and activate brand advocates from social mentions</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => linkMentions.mutate()}
                disabled={linkMentions.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <Link2 className={`w-4 h-4 ${linkMentions.isPending ? 'animate-spin' : ''}`} />
                Link Mentions
              </button>
              <button
                onClick={() => recalculateScores.mutate(undefined)}
                disabled={recalculateScores.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${recalculateScores.isPending ? 'animate-spin' : ''}`} />
                Recalculate Scores
              </button>
            </div>
          </div>

          {/* View Toggle & Search */}
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setView('mentions')}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  view === 'mentions' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
              >
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Mentions
              </button>
              <button
                onClick={() => setView('advocates')}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  view === 'advocates' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                Advocates
              </button>
            </div>

            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={view === 'mentions' ? 'Search mentions...' : 'Search advocates...'}
                value={view === 'mentions' ? mentionFilters.search || '' : advocateFilters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-6">
        <div className="flex gap-6">
          {/* Filters Sidebar */}
          <div className="w-64 shrink-0">
            {view === 'mentions' ? (
              <MentionFiltersPanel
                filters={mentionFilters}
                filterOptions={mentionsData?.filterOptions}
                onChange={(f) => setMentionFilters(prev => ({ ...prev, ...f, page: 1 }))}
              />
            ) : (
              <AdvocateFiltersPanel
                filters={advocateFilters}
                onChange={(f) => setAdvocateFilters(prev => ({ ...prev, ...f, page: 1 }))}
              />
            )}
          </div>

          {/* Results */}
          <div className="flex-1">
            {view === 'mentions' ? (
              <MentionsList 
                data={mentionsData}
                isLoading={mentionsLoading}
                onPageChange={(page) => setMentionFilters(f => ({ ...f, page }))}
                onViewAdvocate={(id) => navigate(`/advocate-opus/advocates/${id}`)}
              />
            ) : (
              <AdvocatesList
                data={advocatesData}
                isLoading={advocatesLoading}
                onPageChange={(page) => setAdvocateFilters(f => ({ ...f, page }))}
                onViewAdvocate={(id) => navigate(`/advocate-opus/advocates/${id}`)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// FILTER PANELS
// ============================================================================

interface MentionFiltersPanelProps {
  filters: MentionFilters
  filterOptions?: {
    platforms: Array<{ value: string; count: number }>
    sentiments: Array<{ value: string; count: number }>
    intents: Array<{ value: string; count: number }>
    statuses: Array<{ value: string; count: number }>
  }
  onChange: (filters: Partial<MentionFilters>) => void
}

function MentionFiltersPanel({ filters, filterOptions, onChange }: MentionFiltersPanelProps) {
  return (
    <div className="space-y-6">
      <FilterSection title="Platform">
        {filterOptions?.platforms.map(p => (
          <FilterOption
            key={p.value}
            label={p.value}
            count={p.count}
            selected={filters.platform === p.value}
            onClick={() => onChange({ platform: filters.platform === p.value ? undefined : p.value })}
          />
        ))}
      </FilterSection>

      <FilterSection title="Sentiment">
        {filterOptions?.sentiments.map(s => (
          <FilterOption
            key={s.value}
            label={s.value}
            count={s.count}
            selected={filters.sentiment === s.value}
            onClick={() => onChange({ sentiment: filters.sentiment === s.value ? undefined : s.value })}
            icon={s.value === 'positive' ? ThumbsUp : s.value === 'negative' ? ThumbsDown : Minus}
          />
        ))}
      </FilterSection>

      <FilterSection title="Status">
        {filterOptions?.statuses.map(s => (
          <FilterOption
            key={s.value}
            label={s.value}
            count={s.count}
            selected={filters.status === s.value}
            onClick={() => onChange({ status: filters.status === s.value ? undefined : s.value })}
          />
        ))}
      </FilterSection>

      <FilterSection title="Advocate Linked">
        <FilterOption
          label="Has advocate"
          selected={filters.has_advocate === true}
          onClick={() => onChange({ has_advocate: filters.has_advocate === true ? undefined : true })}
        />
        <FilterOption
          label="No advocate"
          selected={filters.has_advocate === false}
          onClick={() => onChange({ has_advocate: filters.has_advocate === false ? undefined : false })}
        />
      </FilterSection>
    </div>
  )
}

interface AdvocateFiltersPanelProps {
  filters: AdvocateFilters
  onChange: (filters: Partial<AdvocateFilters>) => void
}

function AdvocateFiltersPanel({ filters, onChange }: AdvocateFiltersPanelProps) {
  return (
    <div className="space-y-6">
      <FilterSection title="Tier">
        {['A', 'B', 'C'].map(tier => (
          <FilterOption
            key={tier}
            label={`Tier ${tier}`}
            selected={filters.tier === tier}
            onClick={() => onChange({ tier: filters.tier === tier ? undefined : tier })}
          />
        ))}
      </FilterSection>

      <FilterSection title="Status">
        {['new', 'invited', 'active', 'inactive', 'declined'].map(status => (
          <FilterOption
            key={status}
            label={status}
            selected={filters.status === status}
            onClick={() => onChange({ status: filters.status === status ? undefined : status })}
          />
        ))}
      </FilterSection>

      <FilterSection title="Revenue">
        <FilterOption
          label="Has revenue"
          selected={filters.has_revenue === true}
          onClick={() => onChange({ has_revenue: filters.has_revenue === true ? undefined : true })}
        />
        <FilterOption
          label="No revenue yet"
          selected={filters.has_revenue === false}
          onClick={() => onChange({ has_revenue: filters.has_revenue === false ? undefined : false })}
        />
      </FilterSection>
    </div>
  )
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border p-4">
      <h4 className="font-medium text-gray-900 mb-3">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

interface FilterOptionProps {
  label: string
  count?: number
  selected: boolean
  onClick: () => void
  icon?: React.ElementType
}

function FilterOption({ label, count, selected, onClick, icon: Icon }: FilterOptionProps) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg ${
        selected ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-50 text-gray-700'
      }`}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="capitalize">{label}</span>
      </div>
      {count !== undefined && (
        <span className="text-xs text-gray-500">{count}</span>
      )}
    </button>
  )
}

// ============================================================================
// MENTIONS LIST
// ============================================================================

interface MentionsListProps {
  data?: {
    mentions: AdvocateMention[]
    pagination: { page: number; totalPages: number; total: number }
  }
  isLoading: boolean
  onPageChange: (page: number) => void
  onViewAdvocate: (id: string) => void
}

function MentionsList({ data, isLoading, onPageChange, onViewAdvocate }: MentionsListProps) {
  if (isLoading) {
    return <LoadingSkeleton count={5} />
  }

  if (!data?.mentions.length) {
    return (
      <div className="bg-white rounded-xl border p-12 text-center">
        <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">No mentions found</p>
        <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or ingest new mentions</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {data.mentions.map((mention) => (
        <MentionCard 
          key={mention.mention_id} 
          mention={mention}
          onViewAdvocate={mention.advocate_id ? () => onViewAdvocate(mention.advocate_id!) : undefined}
        />
      ))}

      <Pagination
        page={data.pagination.page}
        totalPages={data.pagination.totalPages}
        total={data.pagination.total}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function MentionCard({ mention, onViewAdvocate }: { mention: AdvocateMention; onViewAdvocate?: () => void }) {
  const sentimentColor = {
    positive: 'text-green-600 bg-green-50',
    negative: 'text-red-600 bg-red-50',
    neutral: 'text-gray-600 bg-gray-50',
    mixed: 'text-yellow-600 bg-yellow-50',
  }[mention.sentiment || 'neutral']

  return (
    <div className="bg-white rounded-xl border p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {mention.author_avatar_url ? (
            <img src={mention.author_avatar_url} alt="" className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <User className="w-5 h-5 text-gray-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-gray-900">@{mention.author_handle}</div>
            <div className="text-sm text-gray-500">
              {mention.source_platform} • {mention.published_at ? new Date(mention.published_at).toLocaleDateString() : 'Unknown date'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mention.sentiment && (
            <span className={`px-2 py-1 text-xs font-medium rounded capitalize ${sentimentColor}`}>
              {mention.sentiment}
            </span>
          )}
          {mention.advocate_id && (
            <button
              onClick={onViewAdvocate}
              className="px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100"
            >
              View Advocate
            </button>
          )}
        </div>
      </div>

      <p className="text-gray-700 mb-3">{mention.raw_text}</p>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-4 text-gray-500">
          {mention.engagement_metrics.likes !== undefined && (
            <span>👍 {mention.engagement_metrics.likes}</span>
          )}
          {mention.engagement_metrics.comments !== undefined && (
            <span>💬 {mention.engagement_metrics.comments}</span>
          )}
          {mention.engagement_metrics.shares !== undefined && (
            <span>🔄 {mention.engagement_metrics.shares}</span>
          )}
        </div>
        {mention.url && (
          <a
            href={mention.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-purple-600 hover:text-purple-700"
          >
            View Original <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// ADVOCATES LIST
// ============================================================================

interface AdvocatesListProps {
  data?: {
    advocates: Advocate[]
    pagination: { page: number; totalPages: number; total: number }
    summary: { tierCounts: Record<string, number> }
  }
  isLoading: boolean
  onPageChange: (page: number) => void
  onViewAdvocate: (id: string) => void
}

function AdvocatesList({ data, isLoading, onPageChange, onViewAdvocate }: AdvocatesListProps) {
  if (isLoading) {
    return <LoadingSkeleton count={5} />
  }

  if (!data?.advocates.length) {
    return (
      <div className="bg-white rounded-xl border p-12 text-center">
        <User className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">No advocates found</p>
        <p className="text-sm text-gray-400 mt-1">Advocates are created when mentions are linked</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Advocate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tier</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Score</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Mentions</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.advocates.map((adv) => (
              <tr 
                key={adv.advocate_id}
                onClick={() => onViewAdvocate(adv.advocate_id)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {adv.avatar_url ? (
                      <img src={adv.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-gray-900">@{adv.primary_handle}</div>
                      {adv.display_name && (
                        <div className="text-sm text-gray-500">{adv.display_name}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 capitalize text-gray-600">{adv.primary_platform}</td>
                <td className="px-6 py-4 text-center">
                  <TierBadge tier={adv.tier} />
                </td>
                <td className="px-6 py-4 text-center">
                  <StatusBadge status={adv.status} />
                </td>
                <td className="px-6 py-4 text-right font-medium">{adv.score?.toFixed(0) || '-'}</td>
                <td className="px-6 py-4 text-right text-gray-600">{adv.total_mentions}</td>
                <td className="px-6 py-4 text-right font-medium text-green-600">
                  ${adv.total_revenue_generated?.toLocaleString() || '0'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={data.pagination.page}
        totalPages={data.pagination.totalPages}
        total={data.pagination.total}
        onPageChange={onPageChange}
      />
    </div>
  )
}

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

function TierBadge({ tier }: { tier: string | null }) {
  const colors: Record<string, string> = {
    A: 'bg-green-100 text-green-700',
    B: 'bg-yellow-100 text-yellow-700',
    C: 'bg-gray-100 text-gray-700',
  }
  
  if (!tier) return <span className="text-gray-400">-</span>
  
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded ${colors[tier] || colors.C}`}>
      {tier}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    new: 'bg-blue-100 text-blue-700',
    invited: 'bg-purple-100 text-purple-700',
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-700',
    declined: 'bg-red-100 text-red-700',
  }
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${colors[status] || colors.new}`}>
      {status}
    </span>
  )
}

function Pagination({ page, totalPages, total, onPageChange }: { 
  page: number
  totalPages: number
  total: number
  onPageChange: (page: number) => void 
}) {
  return (
    <div className="flex items-center justify-between bg-white rounded-lg border px-4 py-3">
      <span className="text-sm text-gray-500">
        Page {page} of {totalPages} ({total} total)
      </span>
      <div className="flex gap-2">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  )
}

function LoadingSkeleton({ count }: { count: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl border p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gray-200" />
            <div className="space-y-2">
              <div className="h-4 w-32 bg-gray-200 rounded" />
              <div className="h-3 w-24 bg-gray-100 rounded" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 w-full bg-gray-100 rounded" />
            <div className="h-4 w-3/4 bg-gray-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
